import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Plus, Package } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function PortalTab({ resources, isLoading, fetchAllData }) {
  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newType, setNewType] = useState("video");
  const [newUrl, setNewUrl] = useState("");
  const [newCategory, setNewCategory] = useState("installation");

  const addResource = async () => {
    if (!newTitle) return;
    try { 
      await axios.post(`${API}/portal/resources`, { title: newTitle, type: newType, url: newUrl, category: newCategory, order: 0 }); 
      toast.success("Resource added"); 
      setIsAdding(false); 
      setNewTitle(""); 
      setNewUrl(""); 
      fetchAllData(); 
    } catch (e) { toast.error("Failed"); }
  };

  if (isLoading) return <div className="text-center py-12 text-zinc-500">Loading...</div>;

  const grouped = resources.reduce((acc, r) => { 
    if (!acc[r.category]) acc[r.category] = []; 
    acc[r.category].push(r); 
    return acc; 
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="font-heading text-lg uppercase">Portal Resources</h3>
        <Dialog open={isAdding} onOpenChange={setIsAdding}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-2" /> Add</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading uppercase">Add Resource</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase">Title</Label>
                <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase">Type</Label>
                  <Select value={newType} onValueChange={setNewType}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="video">Video</SelectItem>
                      <SelectItem value="manual">Manual</SelectItem>
                      <SelectItem value="faq">FAQ</SelectItem>
                      <SelectItem value="guide">Guide</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase">Category</Label>
                  <Select value={newCategory} onValueChange={setNewCategory}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="installation">Installation</SelectItem>
                      <SelectItem value="operation">Operation</SelectItem>
                      <SelectItem value="troubleshooting">Troubleshooting</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase">URL</Label>
                <Input value={newUrl} onChange={(e) => setNewUrl(e.target.value)} placeholder="https://..." className="mt-1" />
              </div>
              <Button onClick={addResource} className="w-full bg-blue-600 hover:bg-blue-700">Add Resource</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 bg-white rounded-sm border border-zinc-200">
          <Package className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="font-heading text-lg uppercase text-zinc-600 mb-2">No Resources Yet</h3>
          <p className="text-sm text-zinc-500">Add guides and videos for customers.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat} className="bg-white rounded-sm border border-zinc-200 p-4">
              <h4 className="font-heading uppercase text-sm text-zinc-600 mb-3 capitalize">{cat}</h4>
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-zinc-50 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800 text-xs">{item.type}</Badge>
                      <span className="text-sm">{item.title}</span>
                    </div>
                    {item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs hover:underline">View</a>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
